import { useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  type ReviewInternationalWireTransfer,
  approvalsGetApprovalOptions,
  transferSwiftReviewInternationalWireTransfer,
} from '@column/api';
import { Toggle } from '@column/column-ui-kit';

import { ForeignExchange } from '~/app/pages/Transfers/_components/ForeignExchange';
import { TransferReview } from '~/app/pages/Transfers/_components/TransferReview';
import { useInternationalWireReviewQuote } from '~/app/pages/Transfers/_hooks/useInternationalWireReviewQuote';
import {
  buildInternationalWireEvents,
  getInternationalWireReviewFXQuoteDetails,
} from '~/app/pages/Transfers/_utils/internationalWire';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, RenderFields } from '~/components';
import { useAccountNumber, useBankAccount, useCounterparty, useEntity } from '~/hooks';
import { useTransfer } from '~/hooks/useTransfers';
import { useOptimisticTransferUpdate } from '~/lib/api';
import { useNavigate } from '~/lib/navigation';
import { IntlWireTransfer, convertTransferAmount } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ReviewTransferRequest, TransferReviewDecision } from '~/typings/enum';

import { InfoForm, TransferPageHeader } from './_components/Layout';
import { Timeline } from './_components/Timeline';

interface Params {
  id: string;
}

type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

type IntlWireTransferWithNested = Omit<
  IntlWireTransfer,
  | 'instructedAmount'
  | 'instructedCurrencyCode'
  | 'settledAmount'
  | 'settledCurrencyCode'
  | 'fxRate'
  | 'fxQuoteId'
  | 'instructionToBeneficiaryFi'
  | 'chargeBearer'
  | 'uetr'
> &
  Partial<
    Nullable<
      Pick<
        IntlWireTransfer,
        | 'instructedAmount'
        | 'instructedCurrencyCode'
        | 'settledAmount'
        | 'settledCurrencyCode'
        | 'fxRate'
        | 'fxQuoteId'
        | 'instructionToBeneficiaryFi'
        | 'chargeBearer'
        | 'uetr'
      >
    >
  >;

export const PageTransfersInternationalWire: FC = () => {
  const { isAuthorized, isSandbox } = useSessionStore();
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();

  const {
    response: bankAccount,
    isLoading: isBankAccountLoading,
    createRequest: fetchBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: accountNumber,
    isLoading: isAccountNumberLoading,
    createRequest: fetchAccountNumber,
  } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: counterparty,
    isLoading: isCounterpartyLoading,
    createRequest: fetchCounterparty,
  } = useCounterparty({
    onError: (error) => addDangerNotification({ content: error.message }),
  });

  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const { response: entity, createRequest: fetchEntity } = useEntity({
    onSuccess: () => setIsLoadingEntity(false),
    onError: (error) => {
      setIsLoadingEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: originalTransfer,
    isLoading: isTransferLoading,
    createRequest: fetchTransfer,
  } = useTransfer<IntlWireTransfer>('intl-wire')({
    initialParams: { id },
    onSuccess: async (response) => {
      if (response.accountNumberId) {
        fetchAccountNumber({ id: response.accountNumberId });
      }

      try {
        if (response.bankAccountId) {
          const currentBankAccount = await fetchBankAccount({ id: response.bankAccountId });

          if (!currentBankAccount) {
            return;
          }

          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingEntity(true);
          }
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }

      if (response?.counterpartyId) {
        fetchCounterparty({ id: response.counterpartyId });
      }
    },

    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const transfer = originalTransfer as IntlWireTransfer;
  const createdByName = (transfer as IntlWireTransfer & { createdBy?: { name?: string } })?.createdBy?.name;
  const isPendingApproval = transfer?.status === 'pre_review';
  const { data: approvalState, isLoading: isApprovalStateLoading } = useQuery({
    ...approvalsGetApprovalOptions({ path: { object_id: id } }),
    enabled: isPendingApproval,
  });
  const {
    isFXTransfer,
    isLoading: isFXQuoteLoading,
    quote: fxQuote,
    refreshQuote,
  } = useInternationalWireReviewQuote(transfer, isPendingApproval);
  const reviewQuote = isPendingApproval ? fxQuote : undefined;
  const [isReviewLoading, setReviewLoading] = useState(false);
  const optimisticallyUpdateTransfer = useOptimisticTransferUpdate();
  const reviewTransfer = useCallback(
    async (request: ReviewTransferRequest) => {
      setReviewLoading(true);
      try {
        if (request.decision === TransferReviewDecision.Approved && isFXTransfer && !fxQuote) return;
        const body: ReviewInternationalWireTransfer = {
          comment: request.comment,
          decision: request.decision,
          fx_quote_id: request.decision === TransferReviewDecision.Approved && isFXTransfer ? fxQuote?.id : undefined,
        };
        const { data: reviewedTransfer } = await transferSwiftReviewInternationalWireTransfer({
          path: { swift_transfer_id: id },
          body,
          throwOnError: true,
        });
        optimisticallyUpdateTransfer({ id: reviewedTransfer.id, status: reviewedTransfer.status });
        await fetchTransfer({ id });
      } catch (error) {
        if ((error as { code?: string }).code === 'fx_quote_expired') {
          await refreshQuote();
          return;
        }
        addDangerNotification({
          content: (error as Error)?.message ?? 'An error occurred while reviewing the transfer.',
        });
      } finally {
        setReviewLoading(false);
      }
    },
    [addDangerNotification, fetchTransfer, fxQuote, id, isFXTransfer, optimisticallyUpdateTransfer, refreshQuote]
  );
  const formattedTransfer = useMemo(() => (transfer ? { ...transfer } : undefined), [transfer]);
  const formMethods = useForm<IntlWireTransferWithNested>();

  useEffect(() => {
    if (transfer) {
      formMethods.reset(transfer);
    }
  }, [transfer]);

  const isDataLoading = useMemo(() => {
    return isAccountNumberLoading || isBankAccountLoading || isCounterpartyLoading || isTransferLoading;
  }, [isAccountNumberLoading, isBankAccountLoading, isCounterpartyLoading, isTransferLoading]);

  return (
    <>
      <Loading isLoading={isDataLoading} fullHeight>
        {transfer && (
          <TransferPageHeader
            amount={reviewQuote?.sellAmount ?? transfer.amount!}
            currencyCode={reviewQuote?.sellCurrencyCode ?? transfer.currencyCode}
            createdAt={transfer.createdAt}
            isAdding={transfer ? convertTransferAmount('swift', transfer.isIncoming ?? false) > 0 : undefined}
            direction={{ type: transfer.isIncoming ? 'incoming' : 'outgoing' }}
            status={transfer.status!}
            transferId={transfer.id!}
            transferType="swift"
            settledCurrencyAmount={transfer.settledAmount ?? undefined}
            settledCurrencyCode={transfer.settledCurrencyCode}
          />
        )}

        <TransferReview
          bankAccountName={bankAccount?.displayName || bankAccount?.description || 'Unnamed Bank Account'}
          counterpartyName={counterparty?.name || counterparty?.description || 'Unnamed Counterparty'}
          createdByName={createdByName}
          description={transfer?.description ?? undefined}
          details={
            isFXTransfer
              ? getInternationalWireReviewFXQuoteDetails({
                  context: 'details-card',
                  isLoading: isFXQuoteLoading,
                  isSandbox: !!isSandbox,
                  onRetry: refreshQuote,
                  quote: fxQuote,
                })
              : undefined
          }
          isApprovalDisabled={isFXTransfer && (isFXQuoteLoading || !fxQuote)}
          isDisabled={
            !isAuthorized({ permission: 'approveTransfers', permissionLevel: 'write', bankAccount }) ||
            !approvalState?.can_review
          }
          isLoading={isReviewLoading}
          isOpen={!!transfer && isPendingApproval && !isApprovalStateLoading}
          onSubmit={reviewTransfer}
        />

        {bankAccount && accountNumber && counterparty && transfer && !isLoadingEntity && (
          <Timeline
            entries={buildInternationalWireEvents({
              transfer: reviewQuote
                ? { ...transfer, amount: reviewQuote.sellAmount, currencyCode: reviewQuote.sellCurrencyCode }
                : transfer,
              account: {
                accountNumber,
                bankAccount,
                entityType: entity ? (entity.type.toLowerCase() as 'person' | 'business') : undefined,
              },
              counterparty,
            })}
          />
        )}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<IntlWireTransferWithNested>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                    {
                      id: 'idempotencyKey',
                      label: 'Idempotency Key',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} isMonospaced />;
                      },
                    },
                    {
                      id: 'allowOverdraft',
                      label: 'Allow Overdraft',
                      children: ({ value, onChange }) => {
                        const isChecked = typeof value === 'boolean' ? value : false;
                        return <Toggle isChecked={isChecked} onCheckedChange={onChange} isDisabled />;
                      },
                    },
                  ],
                },
              ],
            })}

            {transfer && (
              <ForeignExchange
                data={{
                  settledAmount: reviewQuote?.buyAmount ?? transfer.settledAmount,
                  settledCurrencyCode: reviewQuote?.buyCurrencyCode ?? transfer.settledCurrencyCode,
                  fxRate: reviewQuote?.rate ?? transfer.fxRate,
                  fxQuoteId: reviewQuote?.id ?? transfer.fxQuoteId,
                  fxQuoteStatus: reviewQuote?.status,
                  amount: reviewQuote?.sellAmount ?? transfer.amount,
                  currencyCode: reviewQuote?.sellCurrencyCode ?? transfer.currencyCode,
                  instructedAmount: transfer.instructedAmount,
                  instructedCurrencyCode: transfer.instructedCurrencyCode,
                  instructionToBeneficiaryFi: transfer.instructionToBeneficiaryFi,
                  chargeBearer: transfer.chargeBearer,
                  uetr: transfer.uetr,
                }}
                isIncoming={transfer.isIncoming}
              />
            )}

            <Details
              label="Transfer Details"
              formattedData={formattedTransfer}
              rawData={transfer as unknown as Record<string, unknown>}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
